import type { IObservableArray } from 'mobx';
import { when } from 'mobx';
import { useEffect, useState } from 'react';

import type { Campaign, Goal, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { ERecommendationStatus } from '@feathr/blackbox';
import { useAccount, useStore, useUser } from '@feathr/extender/state';
import type { ListResponse } from '@feathr/rachis';

import { reconcileAllRecommendations } from './DefaultCampaignPage.utils';

interface IProps {
  campaign: Campaign;
}

interface IUseRecommendationsReturn {
  /** Whether there were active recommendations on initial page load (that may have
   * ultimately been applied or dismissed during reconciliation) */
  hasActiveRecommendationsOnLoad: boolean;
  /** Whether any recommendations were dismissed during this session */
  hasRecommendationsDismissed: boolean;
  /** Whether the recommendations collection is loading */
  isLoading: boolean;
  /** Function to mark that a recommendation was dismissed */
  onMarkRecommendationDismissed: () => void;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
  /** Refreshes the recommendations collection during dismiss operations to prevent component unmounting */
  onDismissRefreshCollection: () => Promise<void>;
  /** The recommendations collection */
  recommendations: ListResponse<Recommendation> | null;
  /** Shows recommendations if it's a TTD campaign, the showCampaignRecommendations flag is
   * enabled, the campaign is published or stopped and the recommendations collection is not loading */
  showRecommendations: boolean;
}

/**
 * Hook to fetch recommendations for a campaign and check if any of their statuses have changed
 * @param campaign - The campaign
 * @returns The recommendations collection
 */
function useRecommendations({ campaign }: Readonly<IProps>): IUseRecommendationsReturn {
  const account = useAccount();
  const user = useUser();
  const { Recommendations, Goals } = useStore();

  const [isLoading, setIsLoading] = useState(true);
  const [recommendations, setRecommendations] = useState<ListResponse<Recommendation> | null>(null);
  const [hasRecommendationsDismissed, setHasRecommendationsDismissed] = useState(false);
  const numRecommendations = !!recommendations?.models?.length;
  const [hasActiveRecommendationsOnLoad, sethasActiveRecommendationsOnLoad] = useState(
    !isLoading && numRecommendations,
  );

  const { isTTDCampaign, isPastEndDate, isPublished, isStopped } = campaign;
  const canUseRecommendations = isTTDCampaign && (isPublished || isStopped) && !isPastEndDate;

  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });

  // Fetch recommendations on initial page load
  useEffect(() => {
    if (canUseRecommendations) {
      getRecommendations(false);
    }
  }, []);

  /**
   * Fetches the recommendations for the campaign
   * @param reset - Whether to reset the recommendations (requery for changes)
   * @param isDismissRefresh - Whether this refresh is due to a dismiss operation
   * @returns The active recommendations
   */
  async function getRecommendations(
    reset = true,
    isDismissRefresh = false,
  ): Promise<ListResponse<Recommendation>> {
    // Only set loading state for non-dismiss refreshes to prevent component unmounting
    if (!isDismissRefresh) {
      setIsLoading(true);
    }

    try {
      const list = Recommendations.list(
        {
          filters: {
            campaign: campaign.id,
            account: account.id,
            status: ERecommendationStatus.Active,
          },
          pagination: {
            page_size: 100,
          },
        },
        { reset },
      );

      await when(() => !list.isPending);

      if (list.models.length > 0) {
        // Track if there were recommendations on initial load to properly display the
        // empty state and confetti explosion
        sethasActiveRecommendationsOnLoad(true);

        await reconcileAllRecommendations({
          campaign: campaign as TTDCampaign,
          recommendations: list.models as IObservableArray<Recommendation>,
          onRefreshCollection: handleRefreshCollection,
          timezone: user.get('timezone'),
          goals: goals.models as IObservableArray<Goal>,
        });
      }

      setRecommendations(list);

      return list;
    } finally {
      if (!isDismissRefresh) {
        setIsLoading(false);
      }
    }
  }

  async function handleRefreshCollection(): Promise<void> {
    await getRecommendations();
  }

  async function handleDismissRefreshCollection(): Promise<void> {
    // Use reset = false to avoid clearing recommendations list during dismiss
    await getRecommendations(false, true);
  }

  function onMarkRecommendationDismissed(): void {
    setHasRecommendationsDismissed(true);
  }

  const showRecommendations = canUseRecommendations && !isLoading;

  return {
    hasActiveRecommendationsOnLoad,
    hasRecommendationsDismissed,
    isLoading,
    onMarkRecommendationDismissed,
    onRefreshCollection: handleRefreshCollection,
    onDismissRefreshCollection: handleDismissRefreshCollection,
    recommendations,
    showRecommendations,
  };
}

export default useRecommendations;
